import GoalsApiService from '@/services/goalsApi';
import NedDocumentApi from '@/services/nedDocumentApi';
import Vue from 'vue';
import Vuex from 'vuex';
import CustomWindow from '../CustomWindow';

declare const window: CustomWindow;

Vue.use(Vuex);

export default new Vuex.Store({
    state: {
        hideNedButton: false,
        initializedGoal: false,
        initializedExperience: false,
        initializedGoalsInfo: false,
        goal: 0,
        experience: 0,
        goalsInfo: null,
        nedWizardStep: -1,
        nedWizardBack: false,
        nedWindowIsOpen: false,
        nedWindowButtonPositionRight: "",
        scrollWidth: ""
    },
    mutations: {
        setHideNedButton(state, value) {
            state.hideNedButton = value;
        },
        setInitializedGoal(state) {
            state.initializedGoal = true;
        },        
        setInitializedExperience(state) {
            state.initializedExperience = true;
        },        
        setInitializedGoalsInfo(state) {
            state.initializedGoalsInfo = true;
        },
        setGoal(state, goal) {
            state.goal = goal;
        },
        setExperience (state, experience) {
            state.experience = experience;
        },
        setGoalsInfo(state, goalsInfo) {
            state.goalsInfo = goalsInfo;
        },
        setNedWizardStep(state, nedWizardStep) {
            state.nedWizardStep = nedWizardStep;
        },
        setNedWizardBack(state, nedWizardBack) {
            state.nedWizardBack = nedWizardBack;
        },
        setNedWindowIsOpen(state, nedWindowIsOpen) {
            state.nedWindowIsOpen = nedWindowIsOpen;
            const nedDocumentApi = new NedDocumentApi();
            if (nedWindowIsOpen) {
                nedDocumentApi.showGoalWindow();
                nedDocumentApi.addPaddingForScroll();
            } else {
                nedDocumentApi.hideGoalWindow();
                nedDocumentApi.removePaddingForScroll();
            }
            state.nedWindowButtonPositionRight = nedDocumentApi.calcPositionForGoalButton();
            state.scrollWidth = nedDocumentApi.getBodyPaddingRightPx();
        },
        setNedWindowButtonPositionRight(state, nedWindowButtonPositionRight) {
            state.nedWindowButtonPositionRight = nedWindowButtonPositionRight;
        },
        setScrollWidth(state, scrollWidth) {
            state.scrollWidth = scrollWidth;
        }
    },
    actions: {
        init() {
            this.dispatch('loadGoal');
            this.dispatch('loadGoalsInfo');
        },
        loadGoal() {
            const apiService = new GoalsApiService();
            apiService
                .getGoal()
                .then((response) => {
                    let goal = 0;
                    let goalName = "";
                    let experience = 0;
                    if (response && response.data && response.data.goal) {
                        goal = Number(response.data.goal);
                        experience = Number(response.data.exp);
                        this.commit('setGoal', goal);
                        this.commit('setExperience', experience);
                        // save to local storage    
                        goalName = this.getters.convertGoalToString(goal);
                        localStorage.setItem("goalName", goalName);
                        localStorage.setItem("experience", experience.toString());
                        localStorage.setItem('isGoalWindowClosed', 'true');
                    } else {
                        goalName = localStorage.getItem("goalName") ?? "";
                        experience = Number(localStorage.getItem("experience") ?? "0");
                        goal = this.getters.convertGoalStringToNumber(goalName);
                        this.commit('setGoal', goal);                        
                        this.commit('setExperience', experience);
                    }
                    let nedWizardStep = -1;
                    if (goal) {
                        nedWizardStep += 2;
                        if (experience) {
                            nedWizardStep++;
                        }
                    }
                    this.commit('setNedWizardStep', nedWizardStep);
                    this.commit('setInitializedGoal');
                    this.commit('setInitializedExperience');
                    if (window.goalCoreInstance) {
                        window.goalCoreInstance.refreshLayoutByGoal(goalName);
                    }
                })
                .catch((error) => {
                    // handle error
                    console.error(error);
                    const goalName = localStorage.getItem("goalName") ?? "";
                    const goal = this.getters.convertGoalStringToNumber(goalName);
                    this.commit('setGoal', goal);
                    this.commit('setInitializedGoal');   
                    if (window.goalCoreInstance) {                 
                        window.goalCoreInstance.refreshLayoutByGoal(goalName);
                    }
                });
        },
        saveGoal(context, goal: number) {   
            this.commit('setGoal', goal);
            const goalName = this.getters.convertGoalToString(goal);
            localStorage.setItem("goalName", goalName);
            this.commit('setExperience', 0);
            localStorage.setItem("experience", "0");
            const apiService = new GoalsApiService();
            apiService
                .setGoal(goal)
                .then((response) => {
                    // okay
                })
                .catch((error) => {
                    // handle error
                    console.error(error);
                });
        },
        saveExperience(context, experience: number) {   
            this.commit('setExperience', experience);
            localStorage.setItem("experience", experience.toString());
            const apiService = new GoalsApiService();
            apiService
                .setExperience(experience)
                .then((response) => {
                    // okay
                })
                .catch((error) => {
                    // handle error
                    console.error(error);
                });
        },
        loadGoalsInfo() {
            const apiService = new GoalsApiService();
            apiService
                .getGoalsInfo()
                .then((response) => {
                    if (response && response.data) {
                        this.commit('setGoalsInfo', response.data);
                    } else {
                        console.error('Can\'t load goals info');
                    }
                    this.commit('setInitializedGoalsInfo');                    
                })
                .catch((error) => {
                    // handle error
                    console.error(error);
                    this.commit('setInitializedGoalsInfo');
                });
        }
    },
    modules: {
    },
    getters: {
        getHideNedButton: state => {
            return state.hideNedButton;
        },
        getInitializedGoal: state => {
            return state.initializedGoal;
        },
        getInitializedExperience: state => {
            return state.initializedExperience;
        },
        getInitializedGoalsInfo: state => {
            return state.initializedGoalsInfo;
        },
        getGoal: state => {
            return state.goal;
        },
        getExperience: state => {
            return state.experience;
        },
        getGoalsInfo: state => {
            return state.goalsInfo;
        },
        getNedWizardStep: state => {
            return state.nedWizardStep;
        },
        getNedWizardBack: state => {
            return state.nedWizardBack;
        },
        getNedWindowIsOpen: state => {
            return state.nedWindowIsOpen;
        },
        getNedWindowButtonPositionRight: state => {
            return state.nedWindowButtonPositionRight;
        },
        getScrollWidth: state => {
            return state.scrollWidth;
        },
        convertGoalToString: (state, getters) => (goal: number) => {
            let result = "";
            switch (goal) {
            case 10: 
                result = "goal-show-ropes";
                break;
            case 20:
                result = "goal-better-sailor";
                break;
            case 30:
                result = "goal-skipper";
                break;
            case 40:
                result = "goal-beep-blue";
                break;
            }
            return result;
        },
        convertGoalStringToNumber: (state, getters) => (goal: string) => {
            let result = 0;
            switch (goal) {
            case "goal-show-ropes":
                result = 10;
                break;
            case "goal-better-sailor":
                result = 20;
                break;
            case "goal-skipper":
                result = 30;
                break;
            case "goal-beep-blue":
                result = 40;
                break;
            }
            return result;
        }
    }
})
